/**
 * TvShapeLines.js  (exported as default from HtmlOrderOverlay.js)
 *
 * Creates SL, TP, and Entry price lines using TradingView's native
 * chart.createShape('horizontal_line') drawing API.
 *
 * WHY THIS IS BETTER THAN AN HTML OVERLAY
 * ─────────────────────────────────────────
 * • TV renders the line inside its own canvas → it ALWAYS stays at
 *   the exact price, even during zoom, scroll, pan, symbol change, etc.
 * • No pixel math, no requestAnimationFrame loop, no coordinate space bugs.
 * • Dragging is handled natively by TV – we just read getPoints() after.
 *
 * FEATURES
 * ─────────
 * • disableSelection: false  → line can be clicked and dragged by the user
 * • disableSave: true        → lines are not saved in the chart layout
 * • disableUndo: true        → lines are not added to the undo stack
 * • showInObjectsTree: false → lines are hidden from the objects tree
 * • Magnet disabled globally while lines exist (restored on unmount)
 * • Drawing toolbar is cleared immediately after selection (no style popup)
 * • Full modern color styling: colored line + label + price badge
 */

import { useEffect, useRef, useCallback } from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Get a stable "current bar" time for createShape point.
 * horizontal_line ignores the time value (it spans the full chart)
 * but the API still needs a valid timestamp.
 */
function getNowBarTime(tvWidgetRef) {
    try {
        const chart = tvWidgetRef.current?.activeChart?.();
        // Use the chart's visible range right edge, or Date.now()
        const range = chart?.getVisibleRange?.();
        if (range?.to) return range.to;
    } catch { /* ignore */ }
    return Math.floor(Date.now() / 1000);
}

/**
 * Build createShape overrides for a horizontal_line.
 *
 * Overrides doc keys:  linetoolhorzline.*
 *   linecolor    – line color
 *   linestyle    – 0=solid, 1=dotted, 2=dashed
 *   linewidth    – px
 *   showPrice    – show price bubble on y-axis
 *   textcolor    – label text color
 *   bold         – label bold
 *   fontsize     – label font size
 *   horzLabelsAlign – label horizontal alignment
 *   vertLabelsAlign – label vertical alignment (top/middle/bottom)
 */
// ─── Per-line-type color palette ─────────────────────────────────────────────
// lineColor  = the dashed line itself
// textColor  = the label text (slightly lighter/brighter for contrast)
const LINE_COLORS = {
    tp: { line: '#089981', text: '#089981' },  // solid TV teal
    sl: { line: '#F23645', text: '#F23645' },  // solid TV red
    buyEntry: { line: '#2962FF', text: '#2962FF' },  // solid TV blue
    selEntry: { line: '#F23645', text: '#F23645' },  // solid TV red
};

/**
 * Build overrides for a linetoolhorzline shape.
 *
 * horzLabelsAlign 'right'  → label appears on the RIGHT side, near the price axis
 * vertLabelsAlign 'top'    → label sits ABOVE the dashed line (not on top of it)
 */
function getProperties(lineColor, textColor) {
    return {
        linecolor: lineColor,
        linestyle: 2,        // dashed
        linewidth: 1,        // modern TV thin line
        showPrice: true,
        textcolor: textColor,
        bold: false,         // normal text weight
        fontsize: 12,        // modern TV font size
        horzLabelsAlign: 'right',  // ← near price axis
        vertLabelsAlign: 'bottom', // ← text sits naturally on the line
    };
}

function buildOverrides(lineColor, textColor) {
    const props = getProperties(lineColor, textColor);
    const overrides = {};
    for (const [key, val] of Object.entries(props)) {
        overrides[`linetoolhorzline.${key}`] = val;
    }
    return overrides;
}

// ─── Component ────────────────────────────────────────────────────────────────

const TvShapeLines = ({
    openTrades,
    setOpenTrades,
    tvWidgetRef,
    currSymbol,
    currRate,
    toDisplayAmount,
    LOT_MULTIPLIER,
}) => {
    /**
     * shapeMap: { [tradeId]: { entry: EntityId|null, tp: EntityId|null, sl: EntityId|null } }
     * Holds the TV EntityId for each created shape so we can update / remove them.
     */
    const shapeMap = useRef({});

    /**
     * dragging: Set<EntityId> — tracks which shapes are currently being dragged.
     * We skip setPoints() for dragged shapes to avoid fighting TV's own drag.
     */
    const dragging = useRef(new Set());

    // ── Price updater (called when user drags a line) ─────────────────────────
    const updateTradePrice = useCallback((tradeId, lineType, newPrice) => {
        if (newPrice == null || !isFinite(newPrice)) return;
        setOpenTrades(prev =>
            prev.map(t => {
                if (t.id !== tradeId) return t;
                if (lineType === 'tp') return { ...t, tpPrice: newPrice };
                if (lineType === 'sl') return { ...t, slPrice: newPrice };
                if (lineType === 'entry') return { ...t, price: newPrice };
                return t;
            })
        );
    }, [setOpenTrades]);

    // ── Remove all shapes (used on unmount & chart reset) ─────────────────────
    const removeAllShapes = useCallback(() => {
        try {
            const chart = tvWidgetRef.current?.activeChart?.();
            if (!chart) return;
            Object.values(shapeMap.current).forEach(slot => {
                ['entry', 'tp', 'sl'].forEach(k => {
                    if (slot[k]) {
                        try { chart.removeEntity(slot[k]); } catch { /* already gone */ }
                        slot[k] = null;
                    }
                });
            });
        } catch { /* ignore */ }
        shapeMap.current = {};
    }, [tvWidgetRef]);

    // ── Disable magnet while our lines are active ─────────────────────────────
    useEffect(() => {
        let prevMagnet = null;
        try {
            const widget = tvWidgetRef.current;
            if (widget?.activeChart) {
                const mv = widget.activeChart()?.magnetEnabled?.();
                if (mv) {
                    prevMagnet = mv.value();
                    mv.setValue(false);
                }
            }
        } catch { /* ignore */ }

        return () => {
            // Restore previous magnet state on unmount
            try {
                const widget = tvWidgetRef.current;
                if (prevMagnet !== null && widget?.activeChart) {
                    const mv = widget.activeChart()?.magnetEnabled?.();
                    if (mv) mv.setValue(prevMagnet);
                }
            } catch { /* ignore */ }
        };
    }, [tvWidgetRef]);

    // ── Clear drawing toolbar when anything gets selected ─────────────────────
    useEffect(() => {
        let unsub = null;
        try {
            const chart = tvWidgetRef.current?.activeChart?.();
            if (!chart) return;

            const sel = chart.selection?.();
            if (sel?.onChanged) {
                const sub = sel.onChanged().subscribe(null, () => {
                    // Letting selection remain so native drag square box works
                    // try { sel.clear(); } catch { /* ignore */ }
                });
                unsub = () => {
                    try { sub?.unsubscribe?.(); } catch { /* ignore */ }
                };
            }
        } catch { /* ignore */ }

        return () => { if (unsub) unsub(); };
    }, [tvWidgetRef]);

    // ── Core reconciliation: create / update / remove shapes ──────────────────
    useEffect(() => {
        let chart;
        try {
            chart = tvWidgetRef.current?.activeChart?.();
        } catch { return; }
        if (!chart || typeof chart.createShape !== 'function') return;

        const presentIds = new Set(openTrades.map(t => t.id));

        // 1. Remove shapes for closed trades
        Object.keys(shapeMap.current).forEach(id => {
            if (!presentIds.has(id)) {
                const slot = shapeMap.current[id];
                ['entry', 'tp', 'sl'].forEach(k => {
                    if (slot[k]) {
                        try { chart.removeEntity(slot[k]); } catch { /* already gone */ }
                    }
                });
                delete shapeMap.current[id];
            }
        });

        // 2. Identify what the current bar time is (needed by createShape)
        const barTime = getNowBarTime(tvWidgetRef);

        // 3. Helper: build a label string that shows text + price + pnl
        const makeLabel = (prefix, price, pnl) => {
            const parts = [prefix, price.toFixed(2)];
            if (pnl) parts.push(`(${pnl})`);
            return parts.join('  ');
        };

        // 4. Helper: create ONE shape and register drag listener
        const createLine = (price, label, lineColor, textColor, tradeId, lineType, isLimitEntry = false) => {
            const isLocked = lineType === 'entry' && !isLimitEntry;

            // ⚡ RACE-CONDITION FIX: Mark the slot immediately as 'creating' so
            // that if this effect re-runs before the Promise resolves, we won't
            // call createShape a second (or third) time for the same line.
            if (!shapeMap.current[tradeId]) {
                shapeMap.current[tradeId] = { entry: null, tp: null, sl: null };
            }
            shapeMap.current[tradeId][lineType] = 'creating';

            const shapePromise = chart.createShape(
                { time: barTime, price },
                {
                    shape: 'horizontal_line',
                    text: label,
                    disableSave: true,
                    disableUndo: true,
                    disableSelection: isLocked, // disable drag for regular entry
                    showInObjectsTree: false,
                    lock: isLocked,             // lock position for regular entry
                    zOrder: 'top',
                    overrides: buildOverrides(lineColor, textColor),
                }
            );

            shapePromise.then(entityId => {
                if (!entityId) {
                    // createShape failed — clear sentinel so next render can retry
                    if (shapeMap.current[tradeId]) shapeMap.current[tradeId][lineType] = null;
                    return;
                }

                // Force apply properties immediately as TV `createShape` overrides can delay/fail initially
                try {
                    const api = chart.getShapeById(entityId);
                    if (api) {
                        api.setProperties({
                            text: label,
                            ...getProperties(lineColor, textColor),
                        });
                    }
                } catch { /* ignore */ }

                // Replace sentinel with the real entity ID
                if (!shapeMap.current[tradeId]) {
                    shapeMap.current[tradeId] = { entry: null, tp: null, sl: null };
                }
                shapeMap.current[tradeId][lineType] = entityId;

                // ── Drag detection via selection + polling ─────────────────────
                // When the line is selected (clicked/dragged), we start polling
                // its position and update the trade price on change.
                let pollInterval = null;
                let lastPrice = price;

                const startPolling = () => {
                    if (pollInterval) return; // already polling
                    dragging.current.add(entityId);
                    pollInterval = setInterval(() => {
                        try {
                            const api = chart.getShapeById(entityId);
                            if (!api) { stopPolling(); return; }
                            const pts = api.getPoints();
                            if (pts && pts[0] && pts[0].price !== lastPrice) {
                                lastPrice = pts[0].price;
                                updateTradePrice(tradeId, lineType, lastPrice);
                            }
                        } catch { stopPolling(); }
                    }, 50);
                };

                const stopPolling = () => {
                    if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
                    dragging.current.delete(entityId);
                };

                // Subscribe to chart selection changes to detect drag start/end
                const sel = chart.selection?.();
                if (sel?.onChanged) {
                    sel.onChanged().subscribe(null, () => {
                        const isSelected = sel.contains?.(entityId) ?? false;
                        if (isSelected) {
                            startPolling();
                        } else {
                            stopPolling();
                            // Final confirmed price: read from shape
                            try {
                                const api = chart.getShapeById(entityId);
                                const pts = api?.getPoints?.();
                                if (pts?.[0]?.price != null) {
                                    updateTradePrice(tradeId, lineType, pts[0].price);
                                }
                            } catch { /* ignore */ }
                        }
                    });
                }

            }).catch(err => {
                // Clear sentinel on error so next render can retry
                if (shapeMap.current[tradeId]) shapeMap.current[tradeId][lineType] = null;
                console.warn(`[TvShapeLines] createShape failed (${lineType}):`, err);
            });
        };

        // 5. Helper: update existing shape (price + label)
        const updateLine = (entityId, price, label, lineColor, textColor) => {
            // Skip if still being created asynchronously
            if (!entityId || entityId === 'creating') return;
            try {
                const api = chart.getShapeById(entityId);
                if (!api) return;

                // If not currently selected/dragging, we can update the position as well
                if (!dragging.current.has(entityId)) {
                    api.setPoints([{ time: barTime, price }]);
                }

                // Always update all properties (TradingView expects un-prefixed keys here)
                api.setProperties({
                    text: label,
                    ...getProperties(lineColor, textColor),
                });
            } catch { /* shape gone; will be recreated on next render */ }
        };

        // 6. Process each open trade
        openTrades.forEach(trade => {
            const { id, price, tpPrice, slPrice, side, qty, symbol, orderType, isExecuted } = trade;
            const lotValue = LOT_MULTIPLIER?.[symbol] ?? 1;
            const currency = currSymbol ?? '$';
            const convRate = currRate ?? 1;
            const isBuy = side === 1;
            const isLimit = orderType === 'limit' && !isExecuted;

            if (!shapeMap.current[id]) {
                shapeMap.current[id] = { entry: null, tp: null, sl: null };
            }
            const slot = shapeMap.current[id];

            // ── Entry line ────────────────────────────────────────────────────
            if (price != null) {
                const ec = isBuy ? LINE_COLORS.buyEntry : LINE_COLORS.selEntry;
                const label = makeLabel(
                    (isBuy ? 'Buy' : 'Sell') + (isLimit ? ' Limit' : '') + (qty ? ` ${qty}` : ''),
                    price, null
                );
                if (slot.entry) {
                    updateLine(slot.entry, price, label, ec.line, ec.text);
                } else {
                    createLine(price, label, ec.line, ec.text, id, 'entry', isLimit);
                }
            } else if (slot.entry) {
                try { chart.removeEntity(slot.entry); } catch { /* ignore */ }
                slot.entry = null;
            }

            // ── TP line ───────────────────────────────────────────────────────
            if (isExecuted && tpPrice != null) {
                const rawPnl = (tpPrice - price) * side * qty * lotValue;
                const pnl = (rawPnl >= 0 ? '+' : '') + currency + (rawPnl * convRate).toFixed(2);
                const label = makeLabel('TP', tpPrice, pnl);
                const tc = LINE_COLORS.tp;
                if (slot.tp && slot.tp !== 'creating') {
                    updateLine(slot.tp, tpPrice, label, tc.line, tc.text);
                } else if (!slot.tp) {
                    createLine(tpPrice, label, tc.line, tc.text, id, 'tp');
                }
                // if slot.tp === 'creating': do nothing — wait for promise
            } else if (slot.tp && slot.tp !== 'creating') {
                try { chart.removeEntity(slot.tp); } catch { /* ignore */ }
                slot.tp = null;
            }

            // ── SL line ───────────────────────────────────────────────────────
            if (isExecuted && slPrice != null) {
                const rawPnl = (slPrice - price) * side * qty * lotValue;
                const pnl = (rawPnl >= 0 ? '+' : '') + currency + (rawPnl * convRate).toFixed(2);
                const label = makeLabel('SL', slPrice, pnl);
                const sc = LINE_COLORS.sl;
                if (slot.sl && slot.sl !== 'creating') {
                    updateLine(slot.sl, slPrice, label, sc.line, sc.text);
                } else if (!slot.sl) {
                    createLine(slPrice, label, sc.line, sc.text, id, 'sl');
                }
                // if slot.sl === 'creating': do nothing — wait for promise
            } else if (slot.sl && slot.sl !== 'creating') {
                try { chart.removeEntity(slot.sl); } catch { /* ignore */ }
                slot.sl = null;
            }
        });

    }, [openTrades, tvWidgetRef, currSymbol, currRate, LOT_MULTIPLIER, updateTradePrice]);

    // ── Detect shape deletion by user (Delete key or context menu) ────────────
    // Polls every 500ms to see if a TP/SL shape entityId is still alive.
    // If TV removed it externally, we set the price to null in state so
    // the trade panel can show the "+" restore button.
    useEffect(() => {
        const interval = setInterval(() => {
            let chart;
            try { chart = tvWidgetRef.current?.activeChart?.(); } catch { return; }
            if (!chart) return;

            setOpenTrades(prev => {
                let changed = false;
                const next = prev.map(trade => {
                    if (!trade.isExecuted) return trade;
                    const slot = shapeMap.current[trade.id];
                    if (!slot) return trade;

                    let { tpPrice, slPrice } = trade;

                    // Check TP shape still exists (skip if still being created)
                    if (slot.tp != null && slot.tp !== 'creating' && tpPrice != null) {
                        try {
                            const api = chart.getShapeById(slot.tp);
                            if (!api) {
                                // Shape was deleted externally
                                slot.tp = null;
                                tpPrice = null;
                                changed = true;
                            }
                        } catch {
                            slot.tp = null;
                            tpPrice = null;
                            changed = true;
                        }
                    }

                    // Check SL shape still exists (skip if still being created)
                    if (slot.sl != null && slot.sl !== 'creating' && slPrice != null) {
                        try {
                            const api = chart.getShapeById(slot.sl);
                            if (!api) {
                                slot.sl = null;
                                slPrice = null;
                                changed = true;
                            }
                        } catch {
                            slot.sl = null;
                            slPrice = null;
                            changed = true;
                        }
                    }

                    if (tpPrice !== trade.tpPrice || slPrice !== trade.slPrice) {
                        return { ...trade, tpPrice, slPrice };
                    }
                    return trade;
                });
                return changed ? next : prev;
            });
        }, 500);

        return () => clearInterval(interval);
    }, [tvWidgetRef, setOpenTrades]);

    // ── Subscribe to chart reset events (symbol/TF change) ───────────────────
    useEffect(() => {
        const widget = tvWidgetRef.current;
        if (!widget) return;

        // Some TV versions fire these events on chart reload
        const onReset = () => {
            shapeMap.current = {};  // IDs are stale — TV already cleared them
        };

        try {
            widget.subscribe?.('reset_series', onReset);
            widget.subscribe?.('chart_loaded', onReset);
        } catch { /* ignore */ }

        return () => {
            try {
                widget.unsubscribe?.('reset_series', onReset);
                widget.unsubscribe?.('chart_loaded', onReset);
            } catch { /* ignore */ }
            removeAllShapes();
        };
    }, [tvWidgetRef, removeAllShapes]);

    return null; // No DOM — TradingView renders everything inside its canvas
};

export default TvShapeLines;